<template>
  <div class="carousel-style" v-bind="$attrs" :style="styles" :class="classes">
    <input
      :id="inputId"
      class="carousel-style__input sr-only"
      type="radio"
      :value="inputValue"
      :checked="isChecked"
      :disabled="disabled"
      :name="inputName"
      @change="handleClick"
    />
    <label class="carousel-style__label" :for="inputId">
      <span class="carousel-style__btn" :class="btnClasses">
        <CheckIcon class="carousel-style__check" />
      </span>
      <span class="carousel-style__label-text">
        <slot />
        <Badge
          v-if="showProBadge"
          class="carousel-style__badge"
          content="Pro"
        />
      </span>
    </label>
  </div>
</template>

<script>
import {
  useGroupItem,
  CheckIcon,
  useTheme,
  Badge,
} from '@campsite-bio/component-lib';
import { computed, toRefs } from 'vue';
import uniqueId from 'lodash/uniqueId';
import tinycolor from 'tinycolor2';

import config from '../../../config';

export default {
  components: {
    CheckIcon,
    Badge,
  },

  props: {
    inputValue: {
      type: [Number, String],
      default: null,
    },

    variant: {
      type: String,
      default: null,
    },

    disabled: Boolean,
    showProBadge: Boolean,
  },

  setup(props, { emit }) {
    const { inputValue, variant, disabled } = toRefs(props);
    const theme = useTheme();

    const inputId = computed(() => uniqueId('carousel-style-'));

    const { isInGroup, isSelected, inputName, onToggle } = useGroupItem(
      inputValue,
      'style-group',
    );

    const isChecked = computed(() => {
      return isSelected.value;
    });

    function getVariantClasses(variants, className) {
      if (!variants) return {};

      return variants.split(' ').reduce((accum, variant) => {
        accum[`${className}--${variant}`] = true;
        return accum;
      }, {});
    }

    const classes = computed(() => {
      return {
        'carousel-style--disabled': disabled.value,
        ...getVariantClasses(variant.value, 'carousel-style'),
      };
    });

    const btnClasses = computed(() => {
      return {
        [`carousel-style__btn--${inputValue.value}`]: true,
        ...getVariantClasses(variant.value, 'carousel-style__btn'),
      };
    });

    const styles = computed(() => {
      const primaryColor = tinycolor(theme.value.colors.primary);

      return {
        '--c-active-color': primaryColor.setAlpha(0.65).toRgbString(),
      };
    });

    function handleClick(e) {
      if (disabled.value) return;

      emit('click', e);

      if (isInGroup) onToggle(inputValue.value);
    }

    return {
      classes,
      btnClasses,
      isChecked,
      inputId,
      inputName,
      styles,
      config,
      handleClick,
    };
  },
};
</script>

<style lang="scss" scoped>
.carousel-style {
  text-align: center;

  &--disabled {
    opacity: 0.75;
    pointer-events: none;
  }

  &__input:checked + .carousel-style__label,
  &__input:focus + .carousel-style__label,
  &__label:hover {
    .carousel-style__btn {
      outline: 1px outset var(--c-active-color);
    }
  }

  &__input:checked + .carousel-style__label {
    .carousel-style__check {
      opacity: 1;
    }
  }

  &__label {
    display: block;
    position: relative;

    &:before {
      content: '';
      border: 0.125rem solid transparent;
      border-radius: var(--g-border-radius-standard);
      border-radius: min(
        var(--g-border-radius-standard),
        var(--g-border-radius-min)
      );
      position: absolute;
      left: -0.25rem;
      top: -0.25rem;
      height: calc(var(--button-height) + 0.5rem);
      width: calc(100% + 0.5rem);
      transition: var(--g-transition-speed);
    }
  }

  &__check {
    opacity: 0;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) !important;
    z-index: 1;
  }

  &__btn {
    background: var(--g-color-gray-300);
    border: 2px solid var(--g-color-gray-700);
    font-size: 1.75rem;
    display: flex;
    padding-bottom: 69%;
    position: relative;

    &--short {
      padding-bottom: 50%;
    }

    &--square {
      padding-bottom: 100%;
    }
  }

  &__badge {
    margin-left: 0.35rem;
  }

  &__label-text {
    align-items: center;
    display: inline-flex;
    font-weight: 600;
    margin-top: 0.5rem;
    font-size: 0.825rem;
  }
}
</style>
